export type MapBounds = {
    north: number;
    south: number;
    east: number;
    west: number;
};

const EARTH_RADIUS_MILES = 3958.8;

/**
 * Approximate radius covering the visible map (center → corner), with a small pad.
 * Used for the API nearby query; results are then clipped to exact bounds.
 */
export const radiusMilesFromBounds = (
    center: { lat: number; lng: number },
    bounds: MapBounds,
): number => {
    const cornerDist = haversineMiles(center, {
        lat: bounds.north,
        lng: bounds.east,
    });
    // Slight pad so edge pins aren't dropped by floating-point / API rounding.
    return Math.max(1, cornerDist * 1.05);
};

/** True when a point lies inside the visible map rectangle. */
export const isWithinMapBounds = (
    lat: number,
    lng: number,
    bounds: MapBounds,
): boolean => {
    if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
    if (lat > bounds.north || lat < bounds.south) return false;

    // Normal case: west is left of east.
    if (bounds.west <= bounds.east) {
        return lng >= bounds.west && lng <= bounds.east;
    }

    // Antimeridian-crossing viewport (e.g. west=170, east=-170).
    return lng >= bounds.west || lng <= bounds.east;
};

export const filterBusinessesInBounds = <T extends { latitude: number; longitude: number }>(
    businesses: T[],
    bounds: MapBounds,
): T[] => businesses.filter((b) => isWithinMapBounds(b.latitude, b.longitude, bounds));

/** Fallback radius from zoom when bounds aren't available yet. */
export const calculateRadiusFromZoom = (zoom: number): number => {
    if (zoom <= 9) return 500;
    if (zoom <= 11) return 25;
    if (zoom <= 13) return 10;
    if (zoom <= 15) return 3;
    return 1;
};

/** Step used by the "Zoom out" empty-state CTA. */
export const nextZoomOutLevel = (zoom: number): number =>
    Math.max(zoom - 2, 3);

export const haversineMiles = (
    a: { lat: number; lng: number },
    b: { lat: number; lng: number },
): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Get map bounds and calculate center + radius from the live Google Map.
 */
export const getMapViewParams = (map: google.maps.Map) => {
    const center = map.getCenter();
    const zoom = map.getZoom() || 13;
    const latLngBounds = map.getBounds();

    if (!center) {
        return null;
    }

    const bounds = latLngBounds
        ? {
            north: latLngBounds.getNorthEast().lat(),
            south: latLngBounds.getSouthWest().lat(),
            east: latLngBounds.getNorthEast().lng(),
            west: latLngBounds.getSouthWest().lng(),
        }
        : null;

    const radius = bounds
        ? radiusMilesFromBounds({ lat: center.lat(), lng: center.lng() }, bounds)
        : calculateRadiusFromZoom(zoom);

    return {
        latitude: center.lat(),
        longitude: center.lng(),
        radius,
        zoom,
        bounds,
    };
};
